import { faker } from '@faker-js/faker/locale/es';
import type { Person, Product, Station } from './types';

// Extended WorkOrder model used by the registry. Compatible with
// legacy WorkOrder by populating product/line/startTime.
export interface WorkOrderRow {
  id: string;
  product: string;            // legacy: product description
  productSku: string;
  productName: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'PAUSED' | 'QUEUED' | 'CANCELLED';
  progress: number;
  plannedQty: number;
  producedQty: number;
  defectQty: number;
  line: string;
  station: string;
  stationName: string;
  operatorId: string;
  operatorName: string;
  plant: string;
  startTime: string;
  startDate: string;
  dueDate: string;
  priority: 'URGENT' | 'NORMAL' | 'LOW';
  customer: string;
  bom: Array<{ sku: string; quantity: number }>;
  genealogy: string;
}

const STATUS_MIX: Array<{ value: WorkOrderRow['status']; weight: number }> = [
  { value: 'QUEUED', weight: 0.25 },
  { value: 'IN_PROGRESS', weight: 0.35 },
  { value: 'PAUSED', weight: 0.05 },
  { value: 'COMPLETED', weight: 0.30 },
  { value: 'CANCELLED', weight: 0.05 },
];

const PRIORITY_MIX: Array<{ value: WorkOrderRow['priority']; weight: number }> = [
  { value: 'URGENT', weight: 0.15 },
  { value: 'NORMAL', weight: 0.70 },
  { value: 'LOW', weight: 0.15 },
];

export function generateWorkOrders(
  products: Product[],
  stations: Station[],
  people: Person[],
  count: number = 80,
): WorkOrderRow[] {
  const wos: WorkOrderRow[] = [];
  const now = Date.now();
  const operators = people.filter((p) => p.role === 'operator');

  for (let i = 0; i < count; i++) {
    const product = faker.helpers.arrayElement(products);
    const station = faker.helpers.arrayElement(stations);
    const eligibleOps = operators.filter((o) => o.stationAssignments.includes(station.id));
    const operator = eligibleOps.length > 0 ? faker.helpers.arrayElement(eligibleOps) : faker.helpers.arrayElement(operators);

    const plannedQty = faker.helpers.arrayElement([40, 80, 120, 200, 300, 500]);
    const status = faker.helpers.weightedArrayElement(STATUS_MIX);

    const startOffsetHours =
      status === 'QUEUED'
        ? faker.number.int({ min: 1, max: 48 })       // future
        : faker.number.int({ min: -240, max: -1 });   // past

    const startDate = new Date(now + startOffsetHours * 3600 * 1000);

    let producedQty = 0;
    if (status === 'COMPLETED') {
      producedQty = plannedQty;
    } else if (status === 'IN_PROGRESS' || status === 'PAUSED') {
      const totalSeconds = plannedQty * product.cycleTimeSeconds;
      const elapsedSeconds = -startOffsetHours * 3600;
      const ratio = Math.max(0, Math.min(0.95, elapsedSeconds / totalSeconds));
      producedQty = Math.floor(plannedQty * ratio);
    }

    const defectQty = Math.floor(
      producedQty *
        faker.number.float({
          min: product.defectRate * 0.5,
          max: product.defectRate * 2,
          fractionDigits: 4,
        }),
    );

    const dueDate = new Date(startDate.getTime() + plannedQty * product.cycleTimeSeconds * 1000);
    const idYear = startDate.getFullYear();

    wos.push({
      id: `WO-${idYear}-${String(800 + i).padStart(4, '0')}`,
      product: product.description,
      productSku: product.sku,
      productName: product.description,
      status,
      progress: Math.round((producedQty / plannedQty) * 100),
      plannedQty,
      producedQty,
      defectQty,
      line: station.line,
      station: station.id,
      stationName: station.name,
      operatorId: operator.id,
      operatorName: operator.fullName,
      plant: station.plant,
      startTime: startDate.toISOString(),
      startDate: startDate.toISOString(),
      dueDate: dueDate.toISOString(),
      priority: faker.helpers.weightedArrayElement(PRIORITY_MIX),
      customer: product.customer,
      bom: product.bom,
      genealogy: `SSCC-${faker.string.numeric(18)}`,
    });
  }

  // Sort: in_progress first, then queued, then completed
  const order: Record<WorkOrderRow['status'], number> = {
    IN_PROGRESS: 0,
    PAUSED: 1,
    QUEUED: 2,
    PENDING: 2,
    ON_HOLD: 3,
    COMPLETED: 4,
    CANCELLED: 5,
  };
  return wos.sort((a, b) => order[a.status] - order[b.status]);
}
